import { ActivityType, isProjectRoleEditable } from 'src/types/dto';
import type { ActivityDTO, InspectorDTO, ActivityCompetencyDTO } from 'src/types/dto';
import type { IProjectRole } from 'src/types/project-role';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { Box, Grid, MenuItem, MenuList, Typography } from '@mui/material';

import { CustomPopover } from 'src/components/custom-popover';

import axios, { endpoints } from 'src/utils/axios';
import {
  formatMissingActivityCompetenciesMessage,
  getCollectivelyMissingActivityCompetencies,
  resolveActivityCompetencyRequirementIsActive,
  buildActivityCompetencyRequirementMaps,
} from 'src/utils/activity-competency-coverage';
import { useActivityCompetencyValidationHierarchy } from 'src/hooks/use-competency-hierarchy';

import {
  useGetProjectRoles,
  suggestProjectRolesForInspectors,
  type PreAssignedProjectRole,
} from 'src/actions/project-roles';

import { toast } from 'src/components/snackbar';

import { useAuthContext } from 'src/auth/hooks';

import { LeftHandSide } from 'src/components/visit-allocation-modal/components/left-hand-side';
import { mergeAllocatedInspectorWithPendingSelection } from 'src/components/visit-allocation-modal/allocation-date-label';
import SelectedInspectorsList from 'src/components/visit-allocation-modal/components/selected-inspectors-list';
import { getEarliestActivityAppointment, Shuffle } from 'src/components/shuffle';

import { InspectorListInfiniteScroll } from 'src/components/visit-allocation-modal/components/inspector-list-infinite-scroll';
import { useSSRPaginatedInfiniteScrolling } from 'src/hooks/use-ssr-paginated-infinite-scrolling';
import { usePaginatedApi } from 'src/hooks/use-paginated-api';
import { DEFAULT_PAGE_SIZE } from 'src/types/pagination';
import { formatNumberToMaxPlaces } from 'src/utils/decimal-places';
import { collectBusinessUnitFilterFromActivities } from 'src/utils/project-owner-business-unit';
import { MIN_DURATION_DAYS, roundDuration } from 'src/utils/duration';
import { appointmentDisplayDurationDays } from 'src/utils/appointments';

import {
  AllocationTabFilters,
  type AllocationTabFiltersState,
} from './allocation-tab-filters';
import {
  buildInspectorSelectionFilterSnapshot,
  getActivityDurationDays,
  getAlreadyAllocatedDaysForAllocationBudget,
  getDefaultAllocationDurationDays,
  getDefaultRemainingAllocationDurationDays,
  getDurationDaysAfterInspectorDeselect,
  getDurationDaysForInspectorSelection,
  getRemainingAllocationDurationDays,
  getTotalSelectedDurationDays,
  hasInvalidCountingDurations,
  hasInvalidNonCountingDurations,
  preserveOrRedistributeSelectedDurations,
  inspectorIdsNeedingTeamEarliestAvailabilityReset,
  syncTeamAvailabilityAfterSelectionChange,
  type SelectedInspectorEntry,
} from './allocation-duration';
import { VisitAllocationContentTabs, type VisitAllocationTabValue } from './visit-allocation-content-tabs';
import { ExcludedInspectorsList } from './excluded-inspectors-list';
import { getConsumedUniqueProjectRoleIds, findConflictingUniqueProjectRole } from './unique-project-role';

const MAX_DURATION_DECIMAL_PLACES = 5;

dayjs.extend(utc);

interface VisitAllocationTabProps {
  activity: ActivityDTO;
  allocatedInspectors?: InspectorDTO[];
  onBreakInChangesSubmit?: (solutionId: number) => void;
  onSubmit?: (
    data: {
      inspectorId: number;
      schedule: boolean;
      preferredDate?: string;
      projectRoleId?: number;
      durationDays?: number;
    }[],
    activityId: number,
  ) => void | boolean | Promise<void | boolean>;
  showLoading?: boolean;
  type?: 'allocatedVisit' | 'visit';
  onClose?: () => void;
  onActivityUpdate?: () => void | Promise<unknown>;
}

type FormValues = {
  selectedInspectors: number[];
  selectedAvailabilities: { inspectorId: number; from: string; to: string }[];
};

export function VisitAllocationTab({
  activity,
  allocatedInspectors,
  onBreakInChangesSubmit,
  onSubmit,
  showLoading,
  type,
  onClose,
  onActivityUpdate,
}: VisitAllocationTabProps) {
  const { t } = useTranslation('activities');
  const { user } = useAuthContext();

  const activityCompetencyIds = useMemo(
    () =>
      (activity?.activityCompetencies ?? [])
        .map((activityCompetency) => activityCompetency.competency?.id)
        .filter((id): id is number => id != null),
    [activity?.activityCompetencies],
  );

  const { competencyHierarchy } = useActivityCompetencyValidationHierarchy(
    activityCompetencyIds,
  );

  const handleRescheduleConfirm = useCallback(async () => {
    await onActivityUpdate?.();
  }, [onActivityUpdate]);
  // const [selectedIds, setSelectedRowIds] = useState<GridRowSelectionModel>([]);
  const [selectedInspectors, setSelectedInspectors] = useState<SelectedInspectorEntry[]>([]);
  const [isSavingAllocation, setIsSavingAllocation] = useState(false);
  const [breakInChanges, setBreakInChanges] = useState<boolean>(false);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedAvailabilities, setSelectedAvailabilities] = useState<
    { inspectorId: number; from: string; to: string }[]
  >([]);
  const [hoveredInspectorId, setHoveredInspectorId] = useState<number | undefined>();
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const expectingPageOneAfterResetRef = useRef<boolean>(false);
  // After Approve, keep pending Team rows until refreshed appointments include them.
  // State (not a ref) so the clear effect re-runs after save even if allocated rows
  // were already present from the awaited activity refresh.
  const [pendingClearAfterSaveInspectorIds, setPendingClearAfterSaveInspectorIds] = useState<
    number[] | null
  >(null);
  const [filtersLoading, setFiltersLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<AllocationTabFiltersState>(() => {
    const { businessUnitIds } = collectBusinessUnitFilterFromActivities([activity]);
    return {
      preferredDay: null,
      isOnlyInternal: false,
      isOnlyPreferred: true,
      isRecommended: true,
      selectedCompetencyIds: [],
      selectedBusinessUnitIds: businessUnitIds,
      searchText: '',
      durationDays: getDefaultRemainingAllocationDurationDays(activity),
      maxDistanceKm: '',
    };
  });
  const [recommendedPreferredFallbackDone, setRecommendedPreferredFallbackDone] =
    useState(false);
  const [allocationContentTab, setAllocationContentTab] =
    useState<VisitAllocationTabValue>('available');

  useEffect(() => {
    const defaultDurationDays = getDefaultRemainingAllocationDurationDays(activity);
    const { businessUnitIds } = collectBusinessUnitFilterFromActivities([activity]);
    setPendingClearAfterSaveInspectorIds(null);
    setFilters((prev) => ({
      ...prev,
      preferredDay: null,
      durationDays: defaultDurationDays,
      maxDistanceKm: '',
      selectedBusinessUnitIds: businessUnitIds,
      isOnlyPreferred: true,
      isRecommended: true,
    }));
    setFiltersLoading(true);
    setRecommendedPreferredFallbackDone(false);
    setSelectedInspectors([]);
    setManuallyOverriddenInspectorIds(new Set());
    setAllocationContentTab('available');
  }, [activity?.id]);

  const maxSelectableDurationDays = useMemo(
    () =>
      getRemainingAllocationDurationDays(
        activity,
        selectedInspectors,
        getAlreadyAllocatedDaysForAllocationBudget(activity, selectedInspectors)
      ),
    [activity, selectedInspectors]
  );

  const totalDurationDays = useMemo(() => getActivityDurationDays(activity), [activity]);

  const coveredDurationDays = useMemo(() => {
    if (selectedInspectors.length > 0) {
      return Math.min(getTotalSelectedDurationDays(selectedInspectors), totalDurationDays);
    }

    const appointmentDays = roundDuration(
      (activity?.appointments ?? [])
        .filter(
          (appointment) =>
            appointment.type !== 'blocker' &&
            appointment.projectRole?.countsTowardAudit !== false
        )
        .reduce((sum, appointment) => sum + appointmentDisplayDurationDays(appointment), 0)
    );

    return Math.min(appointmentDays, totalDurationDays);
  }, [activity?.appointments, selectedInspectors, totalDurationDays]);

  const selectedInspectorIds = useMemo(
    () => selectedInspectors.map((si) => si.id),
    [selectedInspectors]
  );

  const accreditationBodyIds = useMemo(
    () => activity?.accreditationBodies?.map((ab) => ab.id) ?? [],
    [activity?.accreditationBodies]
  );

  const canFetchSuitableInspectors = Boolean(activity?.id);

  const isActivityAllocated = useMemo(
    () =>
      (activity.appointments?.length ?? 0) > 0,
    [activity.appointments]
  );

  const showShuffleTab =
    process.env.NEXT_PUBLIC_ENABLE_RESCHEDULE === 'true' &&
    Boolean(onBreakInChangesSubmit) &&
    isActivityAllocated;

  useEffect(() => {
    if (!showShuffleTab && allocationContentTab === 'shuffle') {
      setAllocationContentTab('available');
    }
  }, [showShuffleTab, allocationContentTab]);

  const [inspectorProjectRoles, setInspectorProjectRoles] = useState<Map<number, number | undefined>>(
    new Map()
  );
  // Inspectors whose project role the scheduler manually picked/changed. Their role is
  // never overwritten by auto-suggestion and is always sent explicitly on save.
  const [manuallyOverriddenInspectorIds, setManuallyOverriddenInspectorIds] = useState<Set<number>>(
    new Set()
  );
  const [sortField, setSortField] = useState<'competencies' | 'travelDistance' | null>(null);
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC' | null>(null);
  const [isRefreshingTeamDistance, setIsRefreshingTeamDistance] = useState(false);
  const [teamTravelByInspectorId, setTeamTravelByInspectorId] = useState<
    Record<
      number,
      {
        travelDistance?: number;
        travelTime?: number | null;
        straightLineDistance?: number | null;
      }
    >
  >({});
  const [teamEarliestAvailabilityByInspectorId, setTeamEarliestAvailabilityByInspectorId] =
    useState<
      Record<
        number,
        {
          from: Date;
          to: Date;
          timezoneId?: string;
        }
      >
    >({});
  const { projectRoles, projectRolesError, projectRolesLoading } = useGetProjectRoles();
  const [rolePopover, setRolePopover] = useState<{
    anchorEl: HTMLElement | null;
    inspector: InspectorDTO | null;
  }>({ anchorEl: null, inspector: null });

  const inspectorAppointmentsMap = useMemo(() => {
    const map = new Map<number, number[]>();
    if (type === 'visit' && activity?.appointments) {
      activity.appointments.forEach((app) => {
        const existing = map.get(app.inspector.id) || [];
        existing.push(app.id);
        map.set(app.inspector.id, existing);
      });
    }
    return map;
  }, [activity?.appointments, type]);

  const uniqueAllocatedInspectors = useMemo(() => {
    const inspectorMap = new Map();
    if (type === 'visit') {
      activity?.appointments?.forEach((app) => {
        if (!inspectorMap.has(app.inspector.id)) {
          inspectorMap.set(app.inspector.id, {
            ...app.inspector,
            earliestAvailability: {
              from: app.from,
              to: app.to,
            },
            hotelNights: activity?.appointments?.filter((a) => a.inspector.id === app.inspector.id).reduce((acc, curr) => acc + +(curr.hotelNights ?? 0), 0),
            travelDistance: activity?.appointments?.filter((a) => a.inspector.id === app.inspector.id).reduce((acc, curr) => acc + +(curr.travelToSiteDistance ?? 0) + +(curr.travelFromSiteDistance ?? 0), 0),
            currentProjectRoleId: activity?.appointments?.find((a) => a.inspector.id === app.inspector.id)?.projectRole?.id,
          });
        }
      });
    }
    if (type === 'allocatedVisit') {
      activity?.allocatedInspectors?.forEach((inspector) => {
        if (!inspectorMap.has(inspector.id)) {
          inspectorMap.set(inspector.id, {
            ...inspector,
            earliestAvailability: {
              from: inspector?.slots?.[0]?.from,
              to: inspector?.slots?.[0]?.to,
            },
            travelDistance: inspector.travelDistance,
            hotelNights: inspector.solutionAppointments?.reduce((acc, curr) => acc + +(curr.hotelNights ?? 0), 0),
          });
        }
      });
    }

    return Array.from(inspectorMap.values());
  }, [activity, type]);

  const earliestAppointment = useMemo(
    () => getEarliestActivityAppointment(activity?.appointments),
    [activity?.appointments]
  );

  const {
    data: inspectorsData,
    page: inspectorsPage,
    onPageChange: setInspectorsPage,
    onPageLoaded: onInspectorsDataFetched,
    onReset: onInspectorsReset,
  } = useSSRPaginatedInfiniteScrolling(DEFAULT_PAGE_SIZE);

  const {
    data: inspectorsResponse,
    isLoading: inspectorsLoading,
    isValidating: inspectorsValidating,
    pagination: inspectorsPagination,
    requestKey: inspectorsRequestKey,
    resultKey: inspectorsResultKey,
  } = usePaginatedApi<InspectorDTO>({
    endpoint: endpoints.inspectors.suitableInspectorsByActivityId(activity?.id || 0),
    paginationParams: {
      page: inspectorsPage + 1,
      take: DEFAULT_PAGE_SIZE,
    },
    filterParams: {
      ...(sortField &&
        sortDirection && {
          sortOptions: [{ field: sortField, order: sortDirection }],
        }),
      ...(filters.searchText && { search: filters.searchText }),
    },
    additionalParams: {
      ...((selectedMonth) && {
        targetMonth: selectedMonth,
      }),
      ...((filters.isOnlyPreferred) && { onlyPreferred: 'true' }),
      ...(filters.isRecommended && { onlyRecommended: 'true' }),
      ...(filters.isOnlyInternal && { onlyInternal: 'true' }),
      ...(filters.preferredDay?.isValid() && {
        preferredDay: filters.preferredDay.format('YYYY-MM-DD'),
      }),
      ...(filters.selectedCompetencyIds && filters.selectedCompetencyIds.length > 0 && { competencyIds: filters.selectedCompetencyIds.join(',') }),
      ...(filters.selectedBusinessUnitIds && filters.selectedBusinessUnitIds.length > 0 && { businessUnitIds: filters.selectedBusinessUnitIds.join(',') }),
      ...(Number(filters.durationDays) > 0 && Number.isFinite(Number(filters.durationDays)) && {
        durationDays: Number(filters.durationDays),
      }),
      ...(Number(filters.maxDistanceKm) > 0 &&
        Number.isFinite(Number(filters.maxDistanceKm)) && {
          maxDistanceKm: Number(filters.maxDistanceKm),
        }),
    },
    keepPreviousData: true,
    dedupingInterval: 10_000,
    enabled: canFetchSuitableInspectors,
  });

  const {
    data: excludedInspectorsResponse,
    pagination: excludedInspectorsPagination,
    isLoading: excludedInspectorsLoading,
    isValidating: excludedInspectorsValidating,
  } = usePaginatedApi<InspectorDTO>({
    endpoint: endpoints.inspectors.suitableInspectorsByActivityId(activity?.id || 0),
    paginationParams: {
      page: 1,
      take: 100,
    },
    additionalParams: {
      onlyExcluded: 'true',
      ...(filters.isOnlyInternal && { onlyInternal: 'true' }),
      ...(Number(filters.durationDays) > 0 &&
        Number.isFinite(Number(filters.durationDays)) && {
          durationDays: Number(filters.durationDays),
        }),
    },
    keepPreviousData: true,
    dedupingInterval: 10_000,
    enabled: canFetchSuitableInspectors && allocationContentTab === 'excluded',
  });

  const excludedInspectors = useMemo(
    () => excludedInspectorsResponse ?? [],
    [excludedInspectorsResponse]
  );

  const excludedInspectorsTruncated = useMemo(
    () =>
      excludedInspectorsPagination.itemCount > excludedInspectors.length &&
      excludedInspectors.length >= 100,
    [excludedInspectorsPagination.itemCount, excludedInspectors.length]
  );

  const getInspectorAuthorizations = useCallback(
    (inspectorId: number, inspector?: InspectorDTO) => {
      if (inspector?.authorizations?.length) {
        return inspector.authorizations;
      }
      return inspectorsData?.find((i) => i.id === inspectorId)?.authorizations;
    },
    [inspectorsData],
  );

  const selectedInspectorsWithAuthorizations = useMemo(
    () =>
      selectedInspectors.map((inspector) => ({
        ...inspector,
        authorizations:
          getInspectorAuthorizations(inspector.id, inspector) ?? inspector.authorizations,
      })),
    [getInspectorAuthorizations, selectedInspectors],
  );

  const activityCompetencyCoverageContext = useMemo(() => {
    const requirements = activity?.activityCompetencies ?? [];
    const { competencyLevels, competencyAccreditationBodies } =
      buildActivityCompetencyRequirementMaps(requirements, activity?.accreditationBodies);

    return {
      competencyLevels,
      competencyAccreditationBodies,
      rangeStart: activity?.startDate ? new Date(activity.startDate) : undefined,
      rangeEnd: activity?.dueDate ? new Date(activity.dueDate) : undefined,
    };
  }, [activity?.activityCompetencies, activity?.accreditationBodies, activity?.dueDate, activity?.startDate]);

  const coverageInspectors = useMemo(() => {
    // Pending selection replaces the team on save — use it for live coverage.
    if (selectedInspectorsWithAuthorizations.length > 0) {
      return selectedInspectorsWithAuthorizations;
    }

    // Already allocated with no pending edits: cover from appointment inspectors.
    return uniqueAllocatedInspectors.map((inspector) => ({
      ...inspector,
      authorizations:
        getInspectorAuthorizations(inspector.id, inspector) ?? inspector.authorizations,
    }));
  }, [
    getInspectorAuthorizations,
    selectedInspectorsWithAuthorizations,
    uniqueAllocatedInspectors,
  ]);

  const activeCompetencies = useMemo(
    () =>
      (activity?.activityCompetencies ?? []).map((activityCompetency) => ({
        activityCompetency,
        isActive: resolveActivityCompetencyRequirementIsActive({
          activityCompetency,
          inspectors: coverageInspectors,
          projectRoles,
          rangeStart: activityCompetencyCoverageContext.rangeStart,
          rangeEnd: activityCompetencyCoverageContext.rangeEnd,
          competencyLevels: activityCompetencyCoverageContext.competencyLevels,
          competencyAccreditationBodies:
            activityCompetencyCoverageContext.competencyAccreditationBodies,
          competencyHierarchy,
          collectivelyCoveredCompetencyIds: activity?.collectivelyCoveredCompetencyIds,
          preferBackendCoverageIds: selectedInspectorsWithAuthorizations.length === 0,
        }),
      })),
    [
      activity?.activityCompetencies,
      activity?.collectivelyCoveredCompetencyIds,
      activityCompetencyCoverageContext,
      competencyHierarchy,
      coverageInspectors,
      projectRoles,
      selectedInspectorsWithAuthorizations.length,
    ]
  );

  const selectionListRows = useMemo(() => {
    const selectedById = new Map(selectedInspectors.map((inspector) => [inspector.id, inspector]));
    const allocatedIds = new Set(uniqueAllocatedInspectors.map((inspector) => inspector.id));

    // Prefer pending selection fields (duration, countsTowardAudit) when an already-allocated
    // inspector is also in the new selection — allocateSingle replaces the whole team.
    const mergeTeamTravel = (inspector: InspectorDTO) => ({
      ...inspector,
      ...teamTravelByInspectorId[inspector.id],
      ...(teamEarliestAvailabilityByInspectorId[inspector.id]
        ? { earliestAvailability: teamEarliestAvailabilityByInspectorId[inspector.id] }
        : {}),
    });

    const allocatedRows = uniqueAllocatedInspectors.map((inspector) => {
      const pending = selectedById.get(inspector.id);
      return mergeTeamTravel(
        mergeAllocatedInspectorWithPendingSelection(inspector, pending)
      );
    });
    const pendingOnly = selectedInspectors
      .filter((inspector) => !allocatedIds.has(inspector.id))
      .map(mergeTeamTravel);

    return [...allocatedRows, ...pendingOnly];
  }, [
    uniqueAllocatedInspectors,
    selectedInspectors,
    teamEarliestAvailabilityByInspectorId,
    teamTravelByInspectorId,
  ]);

  useEffect(() => {
    if (!pendingClearAfterSaveInspectorIds?.length) {
      return;
    }
    const allocatedIds = new Set(uniqueAllocatedInspectors.map((inspector) => inspector.id));
    if (!pendingClearAfterSaveInspectorIds.every((id) => allocatedIds.has(id))) {
      return;
    }
    setPendingClearAfterSaveInspectorIds(null);
    setSelectedInspectors([]);
    setInspectorProjectRoles((prev) => {
      const next = new Map<number, number | undefined>();
      uniqueAllocatedInspectors.forEach((inspector) => {
        const roleId = prev.get(inspector.id) ?? inspector.currentProjectRoleId;
        if (roleId != null) {
          next.set(inspector.id, roleId);
        }
      });
      return next;
    });
  }, [uniqueAllocatedInspectors, pendingClearAfterSaveInspectorIds]);

  const prevCanFetchSuitableInspectorsRef = useRef(canFetchSuitableInspectors);

  useEffect(() => {
    if (!canFetchSuitableInspectors) {
      setFiltersLoading(false);
      if (prevCanFetchSuitableInspectorsRef.current) {
        onInspectorsReset();
      }
    }
    prevCanFetchSuitableInspectorsRef.current = canFetchSuitableInspectors;
  }, [canFetchSuitableInspectors, onInspectorsReset]);

  const {
    setValue,
    reset,
    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      selectedInspectors: [],
      selectedAvailabilities: [],
    },
  });

  const sortedRows = useMemo(() => {
    if (inspectorsData) {
      return [...inspectorsData].filter(
        (inspector) => !selectedInspectors.some((si) => si.id === inspector.id)
      );
    }
    return [];
  }, [inspectorsData, selectedInspectors]);

  const totalInspectorsCount = inspectorsData?.length ?? 0;

  const emptyInspectorsMessage = useMemo(() => {
    if (filters.isOnlyPreferred && !filters.isRecommended) {
      return t('allocation.modal.noPreferred');
    }
    if (filters.isRecommended) {
      return t('allocation.modal.noRecommended');
    }
    return t('allocation.modal.noResults');
  }, [filters.isOnlyPreferred, filters.isRecommended, t]);

  
  useEffect(() => {
    if (inspectorsLoading) {
      return;
    }
    const pageNum = inspectorsPagination?.page ?? 1;
    const requestedPage = inspectorsPage + 1;
    if (inspectorsValidating && (pageNum !== requestedPage || filtersLoading)) {
      return;
    }
    if (expectingPageOneAfterResetRef.current && pageNum !== 1) {
      return;
    }
    if (pageNum === 1) {
      expectingPageOneAfterResetRef.current = false;
    }
    setFiltersLoading(false);
    onInspectorsDataFetched(inspectorsResponse, inspectorsPagination);
    if(totalInspectorsCount + (inspectorsResponse?.length ?? 0) < DEFAULT_PAGE_SIZE && inspectorsPagination?.hasNextPage) {
      setInspectorsPage((inspectorsPagination?.page ?? 1) -1); //because we get inspectorsPage + 1 in the paginated api
    }
  }, [inspectorsResponse, inspectorsPagination, onInspectorsDataFetched, setInspectorsPage, totalInspectorsCount, inspectorsLoading, inspectorsValidating, inspectorsPage, filtersLoading]);

  const loadMoreInspectors = useCallback(() => {
    if (
      canFetchSuitableInspectors &&
      inspectorsPagination?.hasNextPage &&
      !inspectorsLoading &&
      !inspectorsValidating &&
      !filtersLoading
    ) {
      setInspectorsPage(inspectorsPage + 1);
    }
  }, [
    canFetchSuitableInspectors,
    inspectorsPage,
    inspectorsPagination?.hasNextPage,
    inspectorsLoading,
    inspectorsValidating,
    setInspectorsPage,
    filtersLoading,
  ]);

  const handleFiltersChange = useCallback(
    (newFilters: AllocationTabFiltersState) => {
      expectingPageOneAfterResetRef.current = true;
      onInspectorsReset();
      setFilters(newFilters);
      setFiltersLoading(true);
    },
    [onInspectorsReset]
  );

  useEffect(() => {
    if (!canFetchSuitableInspectors) {
      return;
    }
    if (inspectorsLoading || inspectorsValidating || filtersLoading) {
      return;
    }
    // SWR key starts null for one frame (not loading, empty meta) — wait until the
    // response key matches the current filter request before deciding to fall back.
    if (!inspectorsRequestKey || inspectorsResultKey !== inspectorsRequestKey) {
      return;
    }
    // Auto-fallback only for the default Recommended + Preferred combo on open.
    if (
      !filters.isRecommended ||
      !filters.isOnlyPreferred ||
      recommendedPreferredFallbackDone
    ) {
      return;
    }
    const pageNum = inspectorsPagination?.page ?? 1;
    if (pageNum !== 1) {
      return;
    }
    const itemCount = inspectorsPagination?.itemCount;
    const pageLength = inspectorsResponse?.length ?? 0;
    const isEmpty = (itemCount ?? pageLength) === 0;
    setRecommendedPreferredFallbackDone(true);
    if (isEmpty) {
      handleFiltersChange({
        ...filters,
        isRecommended: false,
        isOnlyPreferred: false,
      });
    }
  }, [
    canFetchSuitableInspectors,
    filters,
    filtersLoading,
    handleFiltersChange,
    inspectorsLoading,
    inspectorsPagination?.itemCount,
    inspectorsPagination?.page,
    inspectorsRequestKey,
    inspectorsResponse?.length,
    inspectorsResultKey,
    inspectorsValidating,
    recommendedPreferredFallbackDone,
  ]);

  const isSelectionLocked =
    isSavingAllocation || Boolean(pendingClearAfterSaveInspectorIds?.length);

  const closeRolePopover = useCallback(() => {
    setRolePopover({ anchorEl: null, inspector: null });
  }, []);

  const availabilityRefreshRequestIdRef = useRef(0);

  const refreshSelectedEarliestAvailability = useCallback(
    async (selected: SelectedInspectorEntry[]) => {
      if (!activity?.id || selected.length === 0) {
        return;
      }

      const requestId = ++availabilityRefreshRequestIdRef.current;
      const inspectorsWithDuration = selected.filter((inspector) => inspector.selectedDurationDays > 0);
      if (inspectorsWithDuration.length === 0) {
        setIsRefreshingTeamDistance(false);
        setSelectedInspectors((prev) =>
          prev.map((inspector) =>
            inspector.selectedDurationDays > 0
              ? inspector
              : { ...inspector, earliestAvailability: undefined }
          )
        );
        return;
      }
      setIsRefreshingTeamDistance(true);

      try {
        const { data } = await axios.post<
          Array<{
            inspectorId: number;
            earliestAvailability?: {
              from?: string;
              to?: string;
              timezoneId?: string;
            } | null;
            travelDistance?: number;
            travelTime?: number | null;
            straightLineDistance?: number | null;
            isNearbyAllocation?: boolean;
          }>
        >(endpoints.inspectors.earliestAvailabilityByActivityId(activity.id), {
          inspectors: inspectorsWithDuration.map((inspector) => {
              const durationDays = Math.max(inspector.selectedDurationDays, MIN_DURATION_DAYS);
              return {
                inspectorId: inspector.id,
                durationDays,
                countsTowardAudit: inspector.countsTowardAudit !== false,
                ...(inspector.selectionFilters.preferredDay
                  ? { preferredDay: inspector.selectionFilters.preferredDay }
                  : {}),
              };
            }),
        });

        if (requestId !== availabilityRefreshRequestIdRef.current) {
          return;
        }

        const byId = new Map(data.map((item) => [item.inspectorId, item]));
        setTeamTravelByInspectorId((prev) => {
          const next = { ...prev };
          data.forEach((item) => {
            next[item.inspectorId] = {
              ...prev[item.inspectorId],
              ...(item.travelDistance !== undefined
                ? { travelDistance: item.travelDistance }
                : {}),
              ...(item.travelTime !== undefined ? { travelTime: item.travelTime } : {}),
              ...(item.straightLineDistance !== undefined
                ? { straightLineDistance: item.straightLineDistance }
                : {}),
            };
          });
          return next;
        });
        setTeamEarliestAvailabilityByInspectorId((prev) => {
          const next = { ...prev };
          data.forEach((item) => {
            if (item.earliestAvailability?.from && item.earliestAvailability?.to) {
              next[item.inspectorId] = {
                from: new Date(item.earliestAvailability.from),
                to: new Date(item.earliestAvailability.to),
                timezoneId: item.earliestAvailability.timezoneId,
              };
            } else {
              delete next[item.inspectorId];
            }
          });
          return next;
        });
        setSelectedInspectors((prev) =>
          prev.map((inspector): SelectedInspectorEntry => {
            if (!(inspector.selectedDurationDays > 0)) {
              return { ...inspector, earliestAvailability: undefined };
            }
            const updated = byId.get(inspector.id);
            if (!updated) {
              return inspector;
            }

            const next: SelectedInspectorEntry = {
              ...inspector,
              earliestAvailability: updated.earliestAvailability?.from && updated.earliestAvailability?.to
                ? {
                    from: new Date(updated.earliestAvailability.from),
                    to: new Date(updated.earliestAvailability.to),
                    timezoneId: updated.earliestAvailability.timezoneId,
                  }
                : undefined,
            };

            if (updated.travelDistance !== undefined) {
              next.travelDistance = updated.travelDistance;
            }
            if (updated.travelTime !== undefined) {
              (next as SelectedInspectorEntry & { travelTime?: number | null }).travelTime =
                updated.travelTime;
            }
            if (updated.straightLineDistance !== undefined) {
              next.straightLineDistance = updated.straightLineDistance;
            }

            return next;
          })
        );
      } catch (error) {
        console.error('earliestAvailabilityRefresh failed', error);
        if (requestId === availabilityRefreshRequestIdRef.current) {
          toast.error(t('allocation.toast.error.earliestAvailabilityRefresh', {
            defaultValue: 'Could not refresh earliest availability for the selected team',
          }));
        }
      } finally {
        if (requestId === availabilityRefreshRequestIdRef.current) {
          setIsRefreshingTeamDistance(false);
        }
      }
    },
    [activity, t]
  );

  const teamTravelRefreshKey = useMemo(() => {
    const selectedById = new Map(selectedInspectors.map((inspector) => [inspector.id, inspector]));
    const allocatedIds = new Set(uniqueAllocatedInspectors.map((inspector) => inspector.id));
    const activityDays = getActivityDurationDays(activity);

    const segments: string[] = [
      String(activity?.id ?? ''),
      filters.preferredDay?.format('YYYY-MM-DD') ?? '',
    ];

    uniqueAllocatedInspectors.forEach((inspector) => {
      const pending = selectedById.get(inspector.id);
      const countsTowardAudit = pending?.countsTowardAudit ?? true;
      const durationDays =
        pending?.selectedDurationDays ?? (countsTowardAudit === false ? 0 : activityDays);
      segments.push(`${inspector.id}:${durationDays}:${countsTowardAudit}`);
    });

    selectedInspectors
      .filter((inspector) => !allocatedIds.has(inspector.id))
      .forEach((inspector) => {
        segments.push(
          `${inspector.id}:${inspector.selectedDurationDays ?? activityDays}:${inspector.countsTowardAudit ?? true}`,
        );
      });

    return segments.join('|');
  }, [activity, filters.preferredDay, selectedInspectors, uniqueAllocatedInspectors]);

  const teamRefreshEntries = useMemo(() => {
    const selectedById = new Map(selectedInspectors.map((inspector) => [inspector.id, inspector]));
    const allocatedIds = new Set(uniqueAllocatedInspectors.map((inspector) => inspector.id));
    const activityDays = getActivityDurationDays(activity);

    const buildEntry = (
      inspector: InspectorDTO,
      pending?: SelectedInspectorEntry,
    ): SelectedInspectorEntry => {
      const countsTowardAudit = pending?.countsTowardAudit ?? true;
      return {
        ...(pending ?? inspector),
        selectedDurationDays:
          pending?.selectedDurationDays ?? (countsTowardAudit === false ? 0 : activityDays),
        countsTowardAudit,
        selectionFilters:
          pending?.selectionFilters ??
          buildInspectorSelectionFilterSnapshot(filters.preferredDay),
      };
    };

    const entries: SelectedInspectorEntry[] = [];
    uniqueAllocatedInspectors.forEach((inspector) => {
      entries.push(buildEntry(inspector, selectedById.get(inspector.id)));
    });
    selectedInspectors
      .filter((inspector) => !allocatedIds.has(inspector.id))
      .forEach((inspector) => {
        entries.push(buildEntry(inspector, inspector));
      });

    return entries;
  }, [activity, filters.preferredDay, selectedInspectors, uniqueAllocatedInspectors]);

  useEffect(() => {
    availabilityRefreshRequestIdRef.current += 1;
    setTeamTravelByInspectorId({});
    setTeamEarliestAvailabilityByInspectorId({});
    setIsRefreshingTeamDistance(false);
  }, [activity?.id]);

  useEffect(() => {
    if (!activity?.id || teamRefreshEntries.length === 0) {
      return;
    }

    // Desk and on-site both need date-pill refresh when audit days change; travel UI stays desk-gated elsewhere.
    void refreshSelectedEarliestAvailability(teamRefreshEntries);
    // teamRefreshEntries is intentionally keyed by teamTravelRefreshKey only — travel/availability
    // updates must not retrigger this effect or the team table re-renders in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity?.id, activity?.type, refreshSelectedEarliestAvailability, teamTravelRefreshKey]);

  // An explicit day value from the Team table. Unlike add/remove/role-change this does
  // not redistribute: the row is marked manual so later redistributions leave it alone,
  // and only the duration filter is resynced so freed days can be spent on another row.
  const handleInspectorDurationChange = useCallback(
    (inspectorId: number, durationDays: number) => {
      if (isSelectionLocked) {
        return;
      }
      setSelectedInspectors((prev) => {
        const next = prev.map((si) =>
          si.id === inspectorId
            ? {
                ...si,
                selectedDurationDays: durationDays,
                hasManualDuration: true,
                earliestAvailability: undefined,
              }
            : si
        );
        const remaining = getRemainingAllocationDurationDays(activity, next);
        setFilters((f) => ({
          ...f,
          durationDays: getDurationDaysAfterInspectorDeselect(f.durationDays, remaining),
        }));
        return next;
      });
      setTeamEarliestAvailabilityByInspectorId((prev) => {
        const next = { ...prev };
        delete next[inspectorId];
        return next;
      });
    },
    [activity, isSelectionLocked]
  );

  const applySelectionUpdate = useCallback(
    (nextSelected: SelectedInspectorEntry[]) => {
      const alreadyAllocated = getAlreadyAllocatedDaysForAllocationBudget(
        activity,
        nextSelected
      );
      const redistributed = preserveOrRedistributeSelectedDurations(
        activity,
        nextSelected,
        alreadyAllocated
      );
      setSelectedInspectors((prev) => {
        const synced = syncTeamAvailabilityAfterSelectionChange(prev, redistributed);
        const idsToClear = inspectorIdsNeedingTeamEarliestAvailabilityReset(prev, redistributed);
        if (idsToClear.length > 0) {
          setTeamEarliestAvailabilityByInspectorId((mapPrev) => {
            const nextMap = { ...mapPrev };
            idsToClear.forEach((id) => {
              delete nextMap[id];
            });
            return nextMap;
          });
        }
        return synced;
      });
      setValue(
        'selectedInspectors',
        redistributed.map((inspector) => inspector.id),
        { shouldDirty: true }
      );

      const remainingAfter = getRemainingAllocationDurationDays(
        activity,
        redistributed,
        alreadyAllocated
      );
      setFilters((prev) => ({
        ...prev,
        durationDays:
          remainingAfter > 0
            ? formatNumberToMaxPlaces(remainingAfter, MAX_DURATION_DECIMAL_PLACES)
            : getDurationDaysAfterInspectorDeselect(prev.durationDays, remainingAfter),
      }));

    },
    [activity, setValue]
  );

  const handleRemoveInspector = useCallback(
    (inspectorId: number) => {
      if (isSelectionLocked) {
        return;
      }
      const nextSelected = selectedInspectors.filter((si) => si.id !== inspectorId);
      applySelectionUpdate(nextSelected);

      setInspectorProjectRoles((prev) => {
        const next = new Map(prev);
        // Keep roles for already-allocated appointments; clear only pending selection roles.
        const stillAllocated = uniqueAllocatedInspectors.some((i) => i.id === inspectorId);
        if (!stillAllocated) {
          next.delete(inspectorId);
        }
        return next;
      });
      setManuallyOverriddenInspectorIds((prev) => {
        const next = new Set(prev);
        next.delete(inspectorId);
        return next;
      });
    },
    [applySelectionUpdate, isSelectionLocked, selectedInspectors, uniqueAllocatedInspectors]
  );

  const handleRevertSelection = useCallback(() => {
    if (isSelectionLocked) {
      return;
    }
    availabilityRefreshRequestIdRef.current += 1;
    setIsRefreshingTeamDistance(false);
    setTeamTravelByInspectorId({});
    setTeamEarliestAvailabilityByInspectorId({});
    setSelectedInspectors([]);
    setManuallyOverriddenInspectorIds(new Set());
    setPendingClearAfterSaveInspectorIds(null);
    setInspectorProjectRoles((prev) => {
      const next = new Map<number, number | undefined>();
      // Restore roles from existing appointments only.
      if (type === 'visit' && activity?.appointments) {
        activity.appointments.forEach((app) => {
          if (!next.has(app.inspector.id) && app.projectRole?.id) {
            next.set(app.inspector.id, app.projectRole.id);
          }
        });
      } else {
        uniqueAllocatedInspectors.forEach((inspector) => {
          const roleId = prev.get(inspector.id) ?? inspector.currentProjectRoleId;
          if (roleId != null) {
            next.set(inspector.id, roleId);
          }
        });
      }
      return next;
    });
    setValue('selectedInspectors', [], { shouldDirty: false });
    reset({ selectedInspectors: [], selectedAvailabilities: [] });
    setFilters((prev) => ({
      ...prev,
      durationDays: getDefaultRemainingAllocationDurationDays(activity),
    }));
    closeRolePopover();
  }, [
    isSelectionLocked,
    activity,
    closeRolePopover,
    reset,
    setValue,
    type,
    uniqueAllocatedInspectors,
  ]);

  const handleAddInspectorWithRole = useCallback(
    (inspector: InspectorDTO, projectRole: IProjectRole) => {
      if (isSelectionLocked) {
        closeRolePopover();
        return;
      }
      if (selectedInspectors.some((si) => si.id === inspector.id)) {
        closeRolePopover();
        return;
      }

      const alreadyAllocated = getAlreadyAllocatedDaysForAllocationBudget(
        activity,
        selectedInspectors
      );
      const remaining = getRemainingAllocationDurationDays(
        activity,
        selectedInspectors,
        alreadyAllocated
      );

      const selectionFilters = buildInspectorSelectionFilterSnapshot(filters.preferredDay);
      const newEntry: SelectedInspectorEntry = {
        ...inspector,
        earliestAvailability: undefined,
        selectedDurationDays: getDurationDaysForInspectorSelection(
          filters.durationDays,
          remaining,
          Boolean(projectRole.countsTowardAudit)
        ),
        selectionFilters,
        countsTowardAudit: Boolean(projectRole.countsTowardAudit),
      };

      applySelectionUpdate([...selectedInspectors, newEntry]);

      setManuallyOverriddenInspectorIds((prev) => {
        const next = new Set(prev);
        next.add(inspector.id);
        return next;
      });
      setInspectorProjectRoles((prev) => {
        const next = new Map(prev);
        next.set(inspector.id, projectRole.id);
        return next;
      });

      closeRolePopover();
    },
    [
      activity,
      applySelectionUpdate,
      closeRolePopover,
      filters.preferredDay,
      filters.durationDays,
      isSelectionLocked,
      selectedInspectors,
    ]
  );

  const handleChangeSelectedInspectorRole = useCallback(
    (inspectorId: number, projectRoleId: number) => {
      if (isSelectionLocked) {
        return;
      }
      const projectRole = projectRoles.find((role) => role.id === projectRoleId);
      if (!projectRole) {
        return;
      }

      const conflictingUniqueRole = findConflictingUniqueProjectRole(
        inspectorId,
        projectRoleId,
        inspectorProjectRoles,
        projectRoles
      );
      if (conflictingUniqueRole) {
        toast.error(t('common:feedbackCodes.PROJECT_ROLE_UNIQUE_ALREADY_ASSIGNED'), {
          description: t('allocation.toast.error.uniqueProjectRoleAlreadyAssigned', {
            roleName: conflictingUniqueRole.name,
          }),
        });
        return;
      }

      const selected = selectedInspectors.find((si) => si.id === inspectorId);
      if (!selected) {
        return;
      }

      const nextCountsTowardAudit = Boolean(projectRole.countsTowardAudit);
      const countingChanged = selected.countsTowardAudit !== nextCountsTowardAudit;

      setManuallyOverriddenInspectorIds((prev) => {
        const next = new Set(prev);
        next.add(inspectorId);
        return next;
      });
      setInspectorProjectRoles((prev) => {
        const next = new Map(prev);
        next.set(inspectorId, projectRoleId);
        return next;
      });

      if (!countingChanged) {
        return;
      }

      const alreadyAllocated = getAlreadyAllocatedDaysForAllocationBudget(
        activity,
        selectedInspectors
      );
      const remaining = getRemainingAllocationDurationDays(
        activity,
        selectedInspectors.filter((si) => si.id !== inspectorId),
        alreadyAllocated
      );
      const nextSelected = selectedInspectors.map((si) =>
        si.id === inspectorId
          ? {
              ...si,
              countsTowardAudit: nextCountsTowardAudit,
              hasManualDuration: false,
              selectedDurationDays: nextCountsTowardAudit
                ? getDurationDaysForInspectorSelection(
                    filters.durationDays,
                    remaining,
                    true
                  )
                : 0,
            }
          : si
      );
      applySelectionUpdate(nextSelected);
    },
    [
      activity,
      applySelectionUpdate,
      filters.durationDays,
      isSelectionLocked,
      inspectorProjectRoles,
      projectRoles,
      selectedInspectors,
      t,
    ]
  );

  const handleDateCellClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, inspector: InspectorDTO) => {
      if (isSelectionLocked || selectedInspectors.some((si) => si.id === inspector.id)) {
        return;
      }
      setRolePopover({ anchorEl: event.currentTarget, inspector });
    },
    [isSelectionLocked, selectedInspectors]
  );

  const rolePopoverOptions = useMemo(() => {
    const inspector = rolePopover.inspector;
    if (!inspector || !projectRoles.length) {
      return [] as IProjectRole[];
    }

    const consumedUniqueRoleIds = getConsumedUniqueProjectRoleIds(
      inspector.id,
      inspectorProjectRoles,
      projectRoles
    );
    return projectRoles.filter((role) => !consumedUniqueRoleIds.has(role.id));
  }, [inspectorProjectRoles, projectRoles, rolePopover.inspector]);

  const handleMapInspectorSelect = (inspectorId: number) => {
    if (isSelectionLocked) {
      return;
    }
    if (selectedInspectors.some((si) => si.id === inspectorId)) {
      handleRemoveInspector(inspectorId);
    } else {
      const inspector = inspectorsData?.find((i) => i.id === inspectorId);
      if (inspector) {
        setRolePopover({ anchorEl: document.body, inspector });
      }
    }
  };

  const handleSortChange = useCallback(
    (field: 'competencies' | 'travelDistance', direction: 'ASC' | 'DESC' | null) => {
      expectingPageOneAfterResetRef.current = true;
      setSortField(direction ? field : null);
      setSortDirection(direction);
      onInspectorsReset();
    },
    [onInspectorsReset]
  );

  const handleMonthSelection = async (month: string) => {
    setSelectedMonth(month);
    await axios.put(`${endpoints.activities.update}/${activity?.id}?clientId=${user?.client?.id}`, {
      month,
    });
    expectingPageOneAfterResetRef.current = true;
    onInspectorsReset();
  };

  useEffect(() => {
    if (type !== 'visit') {
      return;
    }
    const appointments = activity?.appointments;
    if (!appointments) {
      return;
    }
    // Merge appointment roles into the existing map. Replacing the map wipes roles the
    // scheduler just chose when SWR refreshes appointments that omit nested projectRole.
    setInspectorProjectRoles((prev) => {
      let changed = false;
      const next = new Map(prev);
      appointments.forEach((app) => {
        const roleId = app.projectRole?.id;
        if (roleId != null && next.get(app.inspector.id) !== roleId) {
          next.set(app.inspector.id, roleId);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [activity?.appointments, type]);

  // Auto-suggest is skipped for inspectors who already have a manually chosen role
  // from the date-cell popover (tracked in manuallyOverriddenInspectorIds).
  const selectedInspectorIdsKey = useMemo(
    () => selectedInspectors.map((i) => i.id).sort((a, b) => a - b).join(','),
    [selectedInspectors]
  );

  useEffect(() => {
    if (!activity?.id || projectRoles.length === 0 || selectedInspectors.length === 0) {
      return undefined;
    }

    const inspectorIdsNeedingSuggestion = selectedInspectors
      .map((inspector) => inspector.id)
      .filter(
        (inspectorId) =>
          !manuallyOverriddenInspectorIds.has(inspectorId) &&
          inspectorProjectRoles.get(inspectorId) == null
      );

    if (inspectorIdsNeedingSuggestion.length === 0) {
      return undefined;
    }

    let cancelled = false;

    const fetchSuggestions = async () => {
      const preAssignedRoles: PreAssignedProjectRole[] = [];
      inspectorProjectRoles.forEach((roleId, inspectorId) => {
        if (roleId != null) {
          preAssignedRoles.push({ inspectorId, projectRoleId: roleId });
        }
      });

      try {
        const response = await suggestProjectRolesForInspectors(
          inspectorIdsNeedingSuggestion,
          activity.id,
          preAssignedRoles
        );
        if (cancelled) {
          return;
        }
        setInspectorProjectRoles((prev) => {
          const next = new Map(prev);
          response.inspectors.forEach(({ inspectorId, suggestedProjectRoleId }) => {
            if (
              suggestedProjectRoleId != null &&
              !manuallyOverriddenInspectorIds.has(inspectorId) &&
              next.get(inspectorId) == null
            ) {
              next.set(inspectorId, suggestedProjectRoleId);
            }
          });
          return next;
        });
      } catch {
        // Suggestion is best-effort; the backend assigns roles on save as a fallback.
      }
    };

    fetchSuggestions();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInspectorIdsKey, activity?.id, projectRoles.length]);

  const handleProjectRoleChange = useCallback(async (inspectorId: number, projectRoleId: number | undefined) => {
    if (activity?.status && !isProjectRoleEditable(activity.status)) {
      toast.error(t('common:feedbackCodes.PROJECT_ROLE_LOCKED_AFTER_ALLOCATION'));
      return;
    }

    const conflictingUniqueRole = findConflictingUniqueProjectRole(
      inspectorId,
      projectRoleId,
      inspectorProjectRoles,
      projectRoles
    );
    if (conflictingUniqueRole) {
      toast.error(t('common:feedbackCodes.PROJECT_ROLE_UNIQUE_ALREADY_ASSIGNED'), {
        description: t('allocation.toast.error.uniqueProjectRoleAlreadyAssigned', {
          roleName: conflictingUniqueRole.name,
        }),
      });
      return;
    }

    setManuallyOverriddenInspectorIds((prev) => {
      const next = new Set(prev);
      next.add(inspectorId);
      return next;
    });
    setInspectorProjectRoles((prev) => {
      const newMap = new Map(prev);
      newMap.set(inspectorId, projectRoleId);
      return newMap;
    });

    if (type === 'visit') {
      const appointmentIds = inspectorAppointmentsMap.get(inspectorId);
      if (appointmentIds && appointmentIds.length > 0) {
        try {
          await Promise.all(
            appointmentIds.map((appointmentId) =>
              axios.put(endpoints.appointments.updateProjectRole(appointmentId), {
                projectRoleId: projectRoleId ?? null,
              })
            )
          );
          toast.success(t('allocation.toast.projectRoleUpdated'));
          onActivityUpdate?.();
        } catch (error) {
          const errorKey =
            (error as { key?: string })?.key ?? (error as { message?: string })?.message;
          if (errorKey === 'PROJECT_ROLE_LOCKED_AFTER_ALLOCATION') {
            toast.error(t('common:feedbackCodes.PROJECT_ROLE_LOCKED_AFTER_ALLOCATION'));
          } else if (errorKey === 'PROJECT_ROLE_UNIQUE_ALREADY_ASSIGNED') {
            toast.error(t('common:feedbackCodes.PROJECT_ROLE_UNIQUE_ALREADY_ASSIGNED'));
          } else if (errorKey === 'PROJECT_ROLE_COMPETENCIES_UNCOVERED') {
            toast.error(t('common:feedbackCodes.PROJECT_ROLE_COMPETENCIES_UNCOVERED'));
          } else {
            toast.error(t('allocation.toast.error.projectRoleUpdate'));
          }
          setInspectorProjectRoles((prev) => {
            const newMap = new Map(prev);
            const originalRole = activity?.appointments?.find((a) => a.inspector.id === inspectorId)?.projectRole?.id;
            newMap.set(inspectorId, originalRole);
            return newMap;
          });
        }
      }
    }
  }, [
    type,
    inspectorAppointmentsMap,
    t,
    activity?.appointments,
    activity?.status,
    onActivityUpdate,
    inspectorProjectRoles,
    projectRoles,
  ]);

  const handleTeamListProjectRoleChange = useCallback(
    (inspectorId: number, projectRoleId: number) => {
      if (selectedInspectors.some((inspector) => inspector.id === inspectorId)) {
        handleChangeSelectedInspectorRole(inspectorId, projectRoleId);
        return;
      }
      if (selectedInspectors.length > 0) {
        return;
      }
      void handleProjectRoleChange(inspectorId, projectRoleId);
    },
    [handleChangeSelectedInspectorRole, handleProjectRoleChange, selectedInspectors]
  );

  // Check if all selected inspectors have project roles assigned
  const missingProjectRoles = useMemo(() => {
    if (selectedInspectors.length === 0) return [];

    return selectedInspectors.filter((inspector) => {
      const roleId = inspectorProjectRoles.get(inspector.id);
      return roleId === undefined || roleId === null;
    });
  }, [selectedInspectors, inspectorProjectRoles]);

  const hasAllProjectRoles = missingProjectRoles.length === 0;

  const hasFullDurationCoverage = maxSelectableDurationDays <= 0;

  const allocatedCountingDays = getTotalSelectedDurationDays(selectedInspectors);
  const activityDays = getActivityDurationDays(activity);
  const isOverBudget = allocatedCountingDays > activityDays;
  const countingHasZeroDuration = selectedInspectors
    .filter((inspector) => inspector.countsTowardAudit)
    .some((inspector) => !(Number(inspector.selectedDurationDays) > 0));

  // Compute canSave: must have changes, at least one inspector, and full duration allocated
  // maxSelectableDurationDays clamps at zero, so an over-allocated total is
  // indistinguishable from exact coverage once days are editable by hand.
  const countingDurationsInvalid = useMemo(
    () => hasInvalidCountingDurations(activity, selectedInspectors),
    [activity, selectedInspectors]
  );
  const nonCountingDurationsInvalid = useMemo(
    () => hasInvalidNonCountingDurations(selectedInspectors),
    [selectedInspectors]
  );

  const canSave =
    !isSelectionLocked &&
    isDirty &&
    selectedInspectors.length > 0 &&
    !countingHasZeroDuration &&
    !nonCountingDurationsInvalid &&
    (isOverBudget || breakInChanges || hasFullDurationCoverage);

  const canRevert = !isSelectionLocked && selectedInspectors.length > 0;

  const onSave = async () => {
    // Guard: prevent submission when no inspectors are selected
    if (selectedInspectors.length === 0 || isSelectionLocked) {
      return;
    }

    // Check if all activity competencies are covered by selected inspectors
    const missingCompetencies = getCollectivelyMissingActivityCompetencies({
      activity,
      inspectors: selectedInspectorsWithAuthorizations,
      projectRoles,
      competencyHierarchy,
    });

    if (missingCompetencies.length > 0) {
      toast.error(t('allocation.toast.error.missingCompetencies'), {
        description: `${t('allocation.toast.error.pleaseInclude')}: ${formatMissingActivityCompetenciesMessage(
          missingCompetencies,
          activity.accreditationBodies,
        )}`,
      });
      return;
    }

    if (!hasAllProjectRoles) {
      const inspectorsMissingRoles = missingProjectRoles.map((inspector) => inspector.name).join(', ');
      toast.error(t('allocation.toast.error.missingProjectRole'), {
        description: inspectorsMissingRoles,
      });
      return;
    }

    const uniqueRoleConflict = selectedInspectors
      .map((inspector) =>
        findConflictingUniqueProjectRole(
          inspector.id,
          inspectorProjectRoles.get(inspector.id),
          inspectorProjectRoles,
          projectRoles
        )
      )
      .find((role) => role != null);
    if (uniqueRoleConflict) {
      toast.error(t('common:feedbackCodes.PROJECT_ROLE_UNIQUE_ALREADY_ASSIGNED'), {
        description: t('allocation.toast.error.uniqueProjectRoleAlreadyAssigned', {
          roleName: uniqueRoleConflict.name,
        }),
      });
      return;
    }

    if (countingDurationsInvalid) {
      if (isOverBudget) {
        toast.error(t('allocation.toast.error.overDurationCoverage'), {
          description: t('allocation.toast.error.overDurationCoverageDescription', {
            allocated: formatNumberToMaxPlaces(allocatedCountingDays, MAX_DURATION_DECIMAL_PLACES),
            total: getDefaultAllocationDurationDays(activity),
          }),
        });
      }
      return;
    }

    if (!hasFullDurationCoverage) {
      toast.error(t('allocation.toast.error.incompleteDurationCoverage'), {
        description: t('allocation.toast.error.incompleteDurationCoverageDescription', {
          remaining: formatNumberToMaxPlaces(maxSelectableDurationDays, MAX_DURATION_DECIMAL_PLACES),
          total: getDefaultAllocationDurationDays(activity),
        }),
      });
      return;
    }

    // Only send a projectRoleId when the scheduler manually overrode the suggestion.
    // Newly selected auditors are sent without a role so the backend assigns the best
    // matching roles in one coordinated batch (keeps unique-role tie-breaking correct).
    const selectedInspectorsData = selectedInspectors.map((i) => ({
      inspectorId: i.id,
      schedule: true,
      preferredDate: i.selectionFilters.preferredDay ?? undefined,
      projectRoleId: manuallyOverriddenInspectorIds.has(i.id)
        ? inspectorProjectRoles.get(i.id)
        : undefined,
      durationDays:
        i.selectedDurationDays > 0 ? i.selectedDurationDays : undefined,
    }));

    if (!onSubmit) {
      toast.error(t('allocation.toast.error.save'));
      return;
    }

    setIsSavingAllocation(true);
    closeRolePopover();
    try {
      const saved = await onSubmit(selectedInspectorsData, activity.id);
      if (saved === false) {
        return;
      }

      // Refresh appointments first so the Team list can show the saved allocation.
      // Do not call handleRevertSelection — it rebuilds inspectorProjectRoles from a
      // stale closure (often empty on first allocation) and can blank the Team table
      // before uniqueAllocatedInspectors is populated.
      await Promise.resolve(onActivityUpdate?.());
      setPendingClearAfterSaveInspectorIds(selectedInspectors.map((inspector) => inspector.id));
      availabilityRefreshRequestIdRef.current += 1;
      setManuallyOverriddenInspectorIds(new Set());
      setValue('selectedInspectors', [], { shouldDirty: false });
      reset({ selectedInspectors: [], selectedAvailabilities: [] });
      closeRolePopover();
      const { businessUnitIds } = collectBusinessUnitFilterFromActivities([activity]);
      setFilters({
        preferredDay: null,
        isOnlyInternal: false,
        isOnlyPreferred: true,
        isRecommended: true,
        selectedCompetencyIds: [],
        selectedBusinessUnitIds: businessUnitIds,
        searchText: '',
        durationDays: getDefaultRemainingAllocationDurationDays(activity),
        maxDistanceKm: '',
      });
      setFiltersLoading(true);
      setRecommendedPreferredFallbackDone(false);
    } finally {
      setIsSavingAllocation(false);
    }
  };

  // Clear timeout on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', px: 2 }}>
      <Grid container sx={{ flexGrow: 1 }}>
        <Grid item xs={12} md={3}>
          <LeftHandSide
            activity={activity}
            activeCompetencies={activeCompetencies}
            selectedMonth={selectedMonth}
            onMonthSelect={handleMonthSelection}
            inspectors={inspectorsData ?? []}
            onInspectorSelect={handleMapInspectorSelect}
            highlightedMarkerId={hoveredInspectorId}
            coveredDurationDays={coveredDurationDays}
            totalDurationDays={totalDurationDays}
            selectedIds={
              [
                ...selectedInspectors.map(si=>si.id),
                ...(allocatedInspectors?.map((inspector) => inspector.id) ?? []),
              ] as number[]
            }
          />
        </Grid>
        <Grid item xs={12} md={9} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <SelectedInspectorsList
            selectedInspectors={selectionListRows}
            activity={activity as ActivityDTO}
            projectRoles={projectRoles}
            projectRolesLoading={projectRolesLoading}
            inspectorProjectRoles={inspectorProjectRoles}
            removableInspectorIds={selectedInspectorIds}
            onRemoveInspector={handleRemoveInspector}
            onProjectRoleChange={handleTeamListProjectRoleChange}
            onInspectorDurationChange={handleInspectorDurationChange}
            onRevert={handleRevertSelection}
            onSave={onSave}
            canSave={canSave}
            canRevert={canRevert}
            isSaving={isSelectionLocked}
            accreditationBodyIds={accreditationBodyIds}
            activityCompetencyIds={activityCompetencyIds}
            competencyHierarchy={competencyHierarchy}
            distanceLoading={isRefreshingTeamDistance}
          />
          <VisitAllocationContentTabs
            showShuffleTab={showShuffleTab}
            activeTab={allocationContentTab}
            onActiveTabChange={setAllocationContentTab}
            availableContent={
              <>
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    mt: 1,
                    p: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {t('allocation.details.filters.findAvailableResource')}
                  </Typography>
                  <AllocationTabFilters
                    onFiltersChange={handleFiltersChange}
                    filters={filters}
                    breakInChangesEnabled={false}
                    onBreakInChangesChange={() => {}}
                    onBreakInChangesSubmit={onBreakInChangesSubmit}
                    activity={activity}
                    maxDurationDays={maxSelectableDurationDays}
                    showBreakInChangesToggle={false}
                  />
                </Box>
                <Box
                  sx={{
                    flexGrow: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    mt: 1,
                  }}
                >
                  <InspectorListInfiniteScroll
                    rows={sortedRows ?? []}
                    siteLocation={activity?.site?.location ?? ''}
                    selectedIds={selectedInspectorIds}
                    onDateCellClick={handleDateCellClick}
                    onHoverInspector={setHoveredInspectorId}
                    isDeskService={activity?.type === ActivityType.DeskService}
                    onEndReached={loadMoreInspectors}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSortChange={handleSortChange}
                    loading={
                      inspectorsLoading ||
                      (filters.isRecommended &&
                        filters.isOnlyPreferred &&
                        !recommendedPreferredFallbackDone)
                    }
                    distanceLoading={inspectorsLoading || inspectorsValidating}
                    emptyMessage={emptyInspectorsMessage}
                    accreditationBodyIds={accreditationBodyIds}
                    activityCompetencyIds={activityCompetencyIds}
                    competencyHierarchy={competencyHierarchy}
                    siteTimezoneId={activity?.site?.timezoneId}
                  />
                </Box>
                <CustomPopover
                  open={Boolean(rolePopover.anchorEl)}
                  anchorEl={rolePopover.anchorEl}
                  onClose={closeRolePopover}
                  slotProps={{ arrow: { placement: 'top-right' } }}
                >
                  <MenuList sx={{ minWidth: 200 }}>
                    {rolePopoverOptions.length === 0 ? (
                      <MenuItem disabled>
                        <Typography variant="body2">
                          {projectRolesLoading
                            ? t('allocation.modal.grid.columns.loadingRoles')
                            : t('allocation.modal.grid.columns.noRolesAvailable')}
                        </Typography>
                      </MenuItem>
                    ) : (
                      rolePopoverOptions.map((role) => (
                        <MenuItem
                          key={role.id}
                          onClick={() => {
                            if (rolePopover.inspector) {
                              handleAddInspectorWithRole(rolePopover.inspector, role);
                            }
                          }}
                        >
                          {role.name}
                          {!role.countsTowardAudit && (
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              ({t('allocation.modal.grid.columns.nonCounting')})
                            </Typography>
                          )}
                        </MenuItem>
                      ))
                    )}
                  </MenuList>
                </CustomPopover>
              </>
            }
            excludedContent={
              <Box
                sx={{
                  flexGrow: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                  mt: 1,
                }}
              >
                <ExcludedInspectorsList
                  rows={excludedInspectors}
                  loading={excludedInspectorsLoading}
                  distanceLoading={excludedInspectorsLoading || excludedInspectorsValidating}
                  siteLocation={activity?.site?.location ?? ''}
                  isDeskService={activity?.type === ActivityType.DeskService}
                  accreditationBodyIds={accreditationBodyIds}
                  activityCompetencyIds={activityCompetencyIds}
                  competencyHierarchy={competencyHierarchy}
                  isTruncated={excludedInspectorsTruncated}
                />
              </Box>
            }
            shuffleContent={
              <Shuffle
                activityId={activity?.id ?? 0}
                currentActivity={activity}
                projectId={activity?.standard?.project?.id ?? 0}
                competencyIds={
                  activity?.activityCompetencies?.map(
                    (activityCompetency) => activityCompetency.competency.id
                  ) ?? []
                }
                appointment={earliestAppointment}
                startDate={activity?.startDate}
                dueDate={activity?.dueDate}
                isDeskService={activity?.type === ActivityType.DeskService}
                onRescheduleConfirm={onActivityUpdate ? handleRescheduleConfirm : undefined}
                siteTimezoneId={activity?.site?.timezoneId}
                onDismiss={onClose}
              />
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
}
